import { useEffect, useState } from 'react';

import { getConfig, sendNotification, subtractArraysOfArrays } from 'utils/helperMethods';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import _ from 'lodash';
import { bumpReportExportTableParser } from 'parser/bumpReportExportTableParser';
import { ConfigKeys } from 'utils/Enums';

const errorMessage: string = 'Something went wrong!';

interface Props {
    url: string;
    method: 'post' | 'get' | 'put' | 'delete';
    queryKey: any[];
    onError?: Function;
    variables?: any;
    appConfig?: any;
    extras?: any;
    queryConfig?: {
        enabled?: boolean;
    };
}

interface outputType {
    data: any;
    loading: boolean;
    error: any;
    tableData: any;
    differenceData: any;
}

function useFetchGraphExportComparison({
    url,
    method,
    onError,
    queryKey,
    variables = {},
    appConfig = {},
    queryConfig = {},
    extras = {},
}: Props): outputType {
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<null | string>(null);
    const [data, setData] = useState<any | null>(null);
    const [tableData, setTableData] = useState<any>(null);
    const [differenceData, setDifferenceData] = useState<any>(null);
    const { decimalForNonPercentage } = getConfig(ConfigKeys.COMMON_CONFIG, appConfig);

    // Queries
    const {
        data: apiData,
        isFetching,
        isSuccess,
        fetchStatus,
        error: apiError,
    } = useQuery({
        queryKey,
        queryFn: async () => {
            //initializing states
            setData(null);
            setError(null);
            setLoading(true);
            setTableData(null);
            setDifferenceData(null);
            try {
                const data = await axios({
                    method,
                    url,
                    data: { ...variables },
                });
                return data;
            } catch (error: any) {
                onFetchError(error?.message);
                return { data: { error: error?.message } };
            }
        },
        ...queryConfig,
        //  enabled: false,
    });

    const apiLoading = isFetching || fetchStatus === 'paused';
    const apiSuccess = !apiLoading && !apiError && isSuccess;

    //error handling
    if (!apiLoading && apiError && !error) {
        if (onError) return onError(apiError);
        onFetchError(apiError);
    }
    //error handling when fetching from cache
    if (apiData?.data && 'error' in apiData?.data && !error) {
        onFetchError(apiData?.data?.error);
        sendNotification('error', 'Failed to load Export data!');
    }

    useEffect(() => {
        //success handling
        if (apiSuccess && apiData?.data && queryConfig.enabled) {
            if (error) setError(null);
            onFetchComplete(apiData?.data);
        }
    }, [apiData?.data, apiSuccess, queryConfig.enabled]);

    //called when fetching is complete
    function onFetchComplete(payload: any) {
        try {
            if ('cy' in payload && payload?.cy?.length > 0 && 'ya' in payload && payload?.ya?.length > 0) {
                const fieldKeyLabelMapping = appConfig?.bumpReport?.fieldKeyLabelMapping || {};
                const bumpChartY2AxisOptions = appConfig?.bumpReport?.bumpChartY2AxisOptions || {};
                const y2Metric = bumpChartY2AxisOptions?.[extras?.volTile?.key];
                const cyExportData: any[][] = payload?.cy;
                const yaExportData: any[][] = payload?.ya;
                const [headers, ...cyMainData] = cyExportData;
                const [yaHeaders, ...yaMainData] = yaExportData;
                const formattedHeader: string[] = [];
                let formattedDifferenceHeader: string[] = [];
                if (headers && Array.isArray(headers)) {
                    headers.forEach((header: string) => {
                        if (header in fieldKeyLabelMapping) {
                            formattedHeader.push(fieldKeyLabelMapping[header]?.label);
                        } else if (
                            y2Metric &&
                            Array.isArray(y2Metric) &&
                            y2Metric.find((item) => item?.value === header)
                        ) {
                            formattedHeader.push(y2Metric.find((item) => item?.value === header)?.label);
                        } else {
                            formattedHeader.push(_.startCase(header));
                        }
                    });
                }
                formattedDifferenceHeader = ['CY Date', 'YA Date', ...formattedHeader.slice(1)];
                // CY
                const { columns: cyColumns, rowsData: cyRowsData } = bumpReportExportTableParser(
                    formattedHeader,
                    cyMainData,
                    decimalForNonPercentage,
                );
                // YA
                const { columns: yaColumns, rowsData: yaRowsData } = bumpReportExportTableParser(
                    formattedHeader,
                    yaMainData,
                    decimalForNonPercentage,
                );
                //DIFFERENCE
                const differenceArray = subtractArraysOfArrays(cyMainData, yaMainData);
                const { columns: differenceColumns, rowsData: differenceRowsData } = bumpReportExportTableParser(
                    formattedDifferenceHeader,
                    differenceArray,
                    decimalForNonPercentage,
                );
                setTableData({
                    cy: { columns: cyColumns, rowsData: cyRowsData },
                    ya: { columns: yaColumns, rowsData: yaRowsData },
                    difference: { columns: differenceColumns, rowsData: differenceRowsData },
                });
                const updatedData = [...cyMainData, [''], ['YA'], ...yaMainData];
                setData({ header: formattedHeader, data: updatedData });
                setDifferenceData({ header: formattedDifferenceHeader, data: differenceArray });
            }
        } catch (error) {
            console.error(errorMessage, error);
            setLoading(false);
        } finally {
            setLoading(false);
        }
    }

    //called when API throws an error
    function onFetchError(error: any = errorMessage) {
        setData(null);
        setTableData(null);
        setDifferenceData(null);
        setLoading(false);
        setError(error);
    }
    const outputData: outputType = {
        loading,
        error,
        data,
        tableData,
        differenceData,
    };
    return outputData;
}

export { useFetchGraphExportComparison };
